import { useMemo } from 'react'
import { mapValueKey } from '@editorUtils'
import { getPointAtPercentage } from '@scene'
import { isNull, reject, toNumber } from 'lodash-es'
import { Height } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { Html } from '@react-three/drei'

interface Props {
  graph: TensileTransmissionGraph
  domains: Domain[]
  anchorChecks: AnchorCheckOnSupport[]
}

const AnchorIndicatorMesh = ({ domains, anchorChecks, graph }: Props) => {
  const guidToDomain = useMemo(() => mapValueKey(domains), [domains])
  const { palette } = useTheme()

  const elementGuidToAnchorCheck = useMemo(
    () => (anchorChecks ? mapValueKey(anchorChecks, 'element_guid') : {}),
    [anchorChecks],
  )

  const supportSceneItems = useMemo(() => {
    return reject(
      graph.element_supports.map(item => {
        const anchorCheck = elementGuidToAnchorCheck[item.guid]

        if (!anchorCheck) {
          return null
        }

        if (anchorCheck.utilization < 1) {
          return null
        }

        const { domain_guid, relative_position } = item

        const domain = guidToDomain[domain_guid]

        if (!domain) {
          return null
        }

        const position = getPointAtPercentage(domain.start, domain.end, toNumber(relative_position))

        return {
          ...item,
          position,
        }
      }),
      el => isNull(el),
    )
  }, [graph.element_supports, elementGuidToAnchorCheck, guidToDomain])

  return (
    <>
      {supportSceneItems.map(item => (
        <Html
          key={item?.element_guid}
          position={item?.position.v}
          center
          zIndexRange={[0, 0]}
          // The DOM overlay sits exactly on the connector — it must not swallow canvas clicks,
          // or over-utilized anchors (the ones carrying this marker) become unclickable.
          style={{ pointerEvents: 'none' }}
        >
          <Height sx={{ color: palette.error.main }} />
        </Html>
      ))}
    </>
  )
}

export default AnchorIndicatorMesh
